import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import { Checkbox, Grid, IconButton } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import SydButton from '../../../../commonDesign/Button'
import Switch from '../../../../molecules/Switch'
import { useSyncedTransactionTypeHierarchy } from '../../../../../api/coreData'
import { DEFAULT_METRIC_GROUPS, EMPTY_HIERARCHY_QUERY, METRIC_OP_OPTIONS } from './constants'

const SAFE_ID = /^[a-zA-Z_][a-zA-Z0-9_-]*$/

const groupCardStyle = {
  border: '1px solid var(--color-neutral-200)',
  borderRadius: '6px',
  padding: '12px',
  marginBottom: '8px'
}

const groupHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px'
}

const ftChecklistStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0 12px'
}

const ftItemStyle = {
  display: 'flex',
  alignItems: 'center',
  fontSize: 'var(--font-size-sm)'
}

const metricTableStyle = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '4px'
}

const metricThStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  color: 'var(--text-color-subtle)',
  textAlign: 'left',
  paddingBottom: '2px'
}

function FinancialTypeChecklist ({ selected, onChange }) {
  const { data: hierarchyResult } = useSyncedTransactionTypeHierarchy(EMPTY_HIERARCHY_QUERY)
  const options = useMemo(() => {
    const groups = hierarchyResult?.data
    if (!groups?.length) return []
    return groups.map(g => ({ value: g.financialTypeId, label: g.financialTypeName }))
  }, [hierarchyResult])

  const selectedSet = useMemo(() => new Set(selected ?? []), [selected])

  const handleToggle = useCallback((ftId) => {
    const next = new Set(selectedSet)
    if (next.has(ftId)) {
      next.delete(ftId)
    } else {
      next.add(ftId)
    }
    onChange(Array.from(next).sort((a, b) => a - b))
  }, [selectedSet, onChange])

  if (!options.length) {
    return <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color-subtle)' }}>Loading financial types...</span>
  }

  return (
    <div style={ftChecklistStyle}>
      {options.map(opt => (
        <label key={opt.value} style={ftItemStyle}>
          <Checkbox
            size='small'
            checked={selectedSet.has(opt.value)}
            onChange={() => handleToggle(opt.value)}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  )
}

FinancialTypeChecklist.propTypes = {
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired
}

function MetricRow ({ metric, onUpdate, onRemove }) {
  return (
    <tr>
      <td>
        <SydInput
          value={metric.label ?? ''}
          onChange={(e) => onUpdate({ ...metric, label: e.target.value })}
          placeholder='Label'
          size='sm'
          minWidth='0px'
        />
      </td>
      <td style={{ width: '120px' }}>
        <SydSelect
          options={METRIC_OP_OPTIONS}
          value={metric.op ?? 'count'}
          onChange={(e) => onUpdate({ ...metric, op: e.target.value })}
          size='sm'
          minWidth='0px'
        />
      </td>
      <td style={{ width: '160px' }}>
        <SydInput
          value={metric.format ?? ''}
          onChange={(e) => onUpdate({ ...metric, format: e.target.value })}
          placeholder='e.g. number, currency'
          size='sm'
          minWidth='0px'
        />
      </td>
      <td style={{ width: '32px' }}>
        <IconButton size='small' onClick={onRemove} title='Remove metric'>
          <DeleteIcon fontSize='small' />
        </IconButton>
      </td>
    </tr>
  )
}

MetricRow.propTypes = {
  metric: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

function MetricGroupCard ({ group, onChange, onRemove }) {
  const handleTitleChange = useCallback((e) => {
    onChange({ ...group, title: e.target.value })
  }, [group, onChange])

  const handleIdChange = useCallback((e) => {
    const raw = e.target.value
    const slug = raw.toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/^-+/, '').replace(/-+/g, '-')
    onChange({ ...group, id: slug })
  }, [group, onChange])

  const handleFtChange = useCallback((fts) => {
    onChange({ ...group, financialTypes: fts })
  }, [group, onChange])

  const handleUseAbsChange = useCallback((e) => {
    onChange({ ...group, useAbsoluteAmount: e.target.checked })
  }, [group, onChange])

  const handleMetricUpdate = useCallback((index, updated) => {
    const next = [...group.metrics]
    next[index] = updated
    onChange({ ...group, metrics: next })
  }, [group, onChange])

  const handleMetricRemove = useCallback((index) => {
    const next = [...group.metrics]
    next.splice(index, 1)
    onChange({ ...group, metrics: next })
  }, [group, onChange])

  const handleAddMetric = useCallback(() => {
    onChange({ ...group, metrics: [...(group.metrics || []), { label: '', op: 'count', format: 'number' }] })
  }, [group, onChange])

  return (
    <div style={groupCardStyle}>
      <div style={groupHeaderStyle}>
        <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{group.title || 'Untitled Group'}</span>
        <IconButton size='small' onClick={onRemove} title='Remove group'>
          <DeleteIcon fontSize='small' />
        </IconButton>
      </div>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <label
            className='flex flex-wrap gap-5 items-center border border-neutral-300 bg-neutral-200 radius-md p-2'
            style={{ cursor: 'pointer' }}
          >
            <Switch
              checked={!!group.useAbsoluteAmount}
              onChange={handleUseAbsChange}
            />
            <div>
              <div className='font-size-sm'>Use Absolute Value for this group</div>
              <div className='font-size-xs'>Details are unaffected</div>
            </div>
          </label>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Title'>
            <SydInput value={group.title ?? ''} onChange={handleTitleChange} size='sm' minWidth='0px' />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='ID' description='Letters, numbers, hyphens, underscores'>
            <SydInput
              value={group.id ?? ''}
              onChange={handleIdChange}
              size='sm'
              minWidth='0px'
              error={group.id && !SAFE_ID.test(group.id)}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={12}>
          <SydLabel label='Financial Types' />
          <FinancialTypeChecklist selected={group.financialTypes} onChange={handleFtChange} />
          {(!group.financialTypes || group.financialTypes.length === 0) ? (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-warning-500, #d97706)' }}>
              No financial types selected — metrics will show 0
            </span>
          ) : null}
        </Grid>
        <Grid item xs={12}>
          <SydLabel label='Metrics' />
          <table style={metricTableStyle}>
            <thead>
              <tr>
                <th style={metricThStyle}>Label</th>
                <th style={{ ...metricThStyle, width: '120px' }}>Operation</th>
                <th style={{ ...metricThStyle, width: '160px' }}>Format</th>
                <th style={{ width: '32px' }} />
              </tr>
            </thead>
            <tbody>
              {(group.metrics || []).map((metric, i) => (
                <MetricRow
                  key={i}
                  metric={metric}
                  onUpdate={(updated) => handleMetricUpdate(i, updated)}
                  onRemove={() => handleMetricRemove(i)}
                />
              ))}
            </tbody>
          </table>
          <SydButton variant='ghost' size='xs' onClick={handleAddMetric}>
            <AddIcon fontSize='small' style={{ marginRight: '4px' }} /> Add Metric
          </SydButton>
        </Grid>
      </Grid>
    </div>
  )
}

MetricGroupCard.propTypes = {
  group: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

export default function MetricGroupsEditor ({ configPrefix, control }) {
  return (
    <Controller
      name={`${configPrefix}.metricGroups`}
      control={control}
      render={({ field }) => {
        const groups = field.value ?? DEFAULT_METRIC_GROUPS

        const handleGroupChange = (index, updated) => {
          const next = [...groups]
          next[index] = updated
          field.onChange(next)
        }

        const handleGroupRemove = (index) => {
          const next = [...groups]
          next.splice(index, 1)
          field.onChange(next)
        }

        const handleAddGroup = () => {
          const id = `group-${Date.now()}`
          field.onChange([...groups, { id, title: '', financialTypes: [], metrics: [{ label: 'Total', op: 'count', format: 'number' }] }])
        }

        const handleResetToDefault = () => {
          field.onChange(DEFAULT_METRIC_GROUPS)
        }

        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <SydLabel label='Metric Groups' style={{ marginBottom: 0 }} />
              <SydButton variant='ghost' size='xs' onClick={handleResetToDefault}>
                Reset to Default
              </SydButton>
            </div>
            {groups.map((group, i) => (
              <MetricGroupCard
                key={group.id || i}
                group={group}
                onChange={(updated) => handleGroupChange(i, updated)}
                onRemove={() => handleGroupRemove(i)}
              />
            ))}
            <SydButton variant='outline' size='sm' onClick={handleAddGroup}>
              <AddIcon fontSize='small' style={{ marginRight: '4px' }} /> Add Group
            </SydButton>
          </div>
        )
      }}
    />
  )
}

MetricGroupsEditor.propTypes = {
  configPrefix: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired
}
